import { Paper, Text, Container, Button, Center, Group, Stack, TextInput, Checkbox, Loader, Divider, Radio } from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import bigLogo from '../../img/big-new-logo.png';
import { visiUserToken } from '../../GeneralFunction';
import SettingsAPI from "../../api/SettingsApi";

// customers should not have to read raw scope strings
const scopeLabels = {
  'campaigns:read': 'Campaigns',
  'tests:read': 'Tests',
  'journeys:read': 'Customer Journeys'
};

const scopeHints = {
  'campaigns:read': 'Campaign and ad performance',
  'tests:read': 'A/B tests and their results',
  'journeys:read': 'How visitors reached a conversion'
};

const scopeLabel = (scope:string) => scopeLabels[scope] ? scopeLabels[scope] : scope;

type Connection = {
  connection_id: string;
  name: string;
  scopes: string[];
  used_by: string;
  created_at: string;
  last_used_at: string;
}

export const McpConnect = () => {

  useDocumentTitle("Visiopt - Connect");

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestId = params.get("request_id") || '';
  const assistantName = params.get("client_name") || 'This assistant';

  const [errorMessage, setErrorMessage] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [connectionName, setConnectionName] = useState(assistantName);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [selectedConnection, setSelectedConnection] = useState('');

  const mcpOAuthUrl = process.env.REACT_APP_MCP_OAUTH_URL;

  useEffect(() => {
    if(requestId === ''){
      setErrorMessage("Missing connect request. Please start again from your AI assistant.");
      setLoading(false);
      return;
    }
    // the login and select member screens send the user back here once a session exists
    if(!visiUserToken()){
      sessionStorage.setItem('mcpRequestId', requestId);
      navigate("/login");
      return;
    }
    loadConnections();
  }, [requestId, navigate]);

  const loadConnections = () => {
    try {
      SettingsAPI.getaiconnectionlist()
      .then(APIResponse => APIResponse.json())
      .then(APIResult => {
        if(APIResult.status === 1){
          setConnections(APIResult.data.connection_list);
          setAvailableScopes(APIResult.data.available_scopes);
          setSelectedScopes(APIResult.data.available_scopes);
        }else{
          setErrorMessage(APIResult.message);
        }
        setLoading(false);
      })
      .catch(error => {
        console.log('error', error);
        setErrorMessage("Could not load your existing connections.");
        setLoading(false);
      });
    } catch (error) {
      console.log('error', error);
      setLoading(false);
    }
  };

  const handleScopeChange = (scope:string, checked:boolean) => {
    setSelectedScopes(checked ? [...selectedScopes, scope] : selectedScopes.filter(s => s !== scope));
  };

  const handleRevoke = (connection_id:string) => {
    try {
      SettingsAPI.revokeaiconnection({ connection_id : connection_id })
      .then(APIResponse => APIResponse.json())
      .then(APIResult => {
        if(APIResult.status === 1){
          setConnections(connections.filter(c => c.connection_id !== connection_id));
        }else{
          setErrorMessage(APIResult.message);
        }
      })
      .catch(error => console.log('error', error));
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleConnect = () => {
    if(!mcpOAuthUrl){
      setErrorMessage("REACT_APP_MCP_OAUTH_URL is not set. Add it to .env and restart the app.");
      return;
    }
    if(selectedConnection === ''){
      if(connectionName.trim() === ''){
        setErrorMessage("Name this connection so you can recognise it later.");
        return;
      }
      if(selectedScopes.length === 0){
        setErrorMessage("Select at least one permission.");
        return;
      }
    }
    setConnecting(true);
    setErrorMessage('');
    try {
      fetch(mcpOAuthUrl+'oauth/mcp-callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id : requestId,
          visi_login_token : visiUserToken(),
          connection_id : selectedConnection,
          connection_name : connectionName.trim(),
          scopes : selectedScopes
        })
      })
      .then(APIResponse => APIResponse.json())
      .then(APIResult => {
        if(APIResult.status === 1){
          sessionStorage.removeItem('mcpRequestId');
          window.location.href = APIResult.redirect_url;
        }else{
          setConnecting(false);
          setErrorMessage(APIResult.message);
        }
      })
      .catch(error => {
        console.log('error', error);
        setConnecting(false);
        setErrorMessage("Could not reach the MCP server at "+mcpOAuthUrl);
      });
    } catch (error) {
      console.log('error', error);
      setConnecting(false);
    }
  };

  const handleLogInAgain = () => {
    if(requestId !== ''){
      sessionStorage.setItem('mcpRequestId', requestId);
    }
    navigate("/logout");
  };

  return (
    <Container size={800} my={40}>
      <Paper p={30} mt={30} radius="8px" style={{ border : '1px solid #dee2e6' }}>
        <Paper w="70%" mx="auto">
          <Center>
            <img src={bigLogo} alt="Company Logo"/>
          </Center>
          <Text ta="center" mt={10} mb={5} fw={700}>Connect your Visiopt account</Text>
          <Text ta="center" c="dimmed" size="sm" mb={20}>
            {assistantName} is asking to read your Visiopt data.
          </Text>

          {errorMessage !== '' && (
            <Text ta="center" c="red" mb={15}>{errorMessage}</Text>
          )}

          {requestId === '' ? (
            <Group position="center">
              <Button onClick={handleLogInAgain}>Log in again</Button>
            </Group>
          ) : loading ? (
            <Center><Loader size="sm"/></Center>
          ) : (
            <>
              {connections.length > 0 && (
                <>
                  <Text size="sm" fw={700} mb={5}>Your connections</Text>
                  <Text size="xs" c="dimmed" mb={10}>
                    Each assistant gets its own connection, so you can revoke one without affecting the others.
                  </Text>
                  <Radio.Group value={selectedConnection} onChange={setSelectedConnection} mb={20}>
                    <Stack spacing={8}>
                      {connections.map(connection => (
                        <Group key={connection.connection_id} position="apart" noWrap>
                          <Radio
                            value={connection.connection_id}
                            label={
                              <div>
                                <Text size="sm">{connection.name}</Text>
                                <Text size="xs" c="dimmed">
                                  {connection.used_by ? 'In use by '+connection.used_by : 'Not connected yet'}
                                  {connection.last_used_at ? ' · Last used '+connection.last_used_at : ''}
                                </Text>
                              </div>
                            }
                          />
                          <Button size="xs" variant="subtle" color="red" onClick={() => handleRevoke(connection.connection_id)}>
                            Revoke
                          </Button>
                        </Group>
                      ))}
                      <Radio value="" label={<Text size="sm">Create a new connection</Text>} />
                    </Stack>
                  </Radio.Group>
                  <Divider mb={20}/>
                </>
              )}

              {selectedConnection === '' && (
              <>
              <Text size="sm" fw={700} mb={10}>New connection</Text>
              <TextInput
                label="Name"
                placeholder="Claude Desktop"
                value={connectionName}
                onChange={(event) => setConnectionName(event.currentTarget.value)}
              />
              <Text size="sm" fw={700} mt={15} mb={5}>Permissions</Text>
              <Stack spacing={5}>
                {availableScopes.map(scope => (
                  <Checkbox
                    key={scope}
                    label={scopeLabel(scope)}
                    description={scopeHints[scope]}
                    checked={selectedScopes.includes(scope)}
                    onChange={(event) => handleScopeChange(scope, event.currentTarget.checked)}
                  />
                ))}
              </Stack>
              </>
              )}
              <Group position="center" mt={25}>
                <Button variant="default" onClick={() => navigate("/dashboard")} disabled={connecting}>
                  Cancel
                </Button>
                <Button onClick={handleConnect} disabled={connecting}>
                  {connecting ? <Loader size="xs" color="white"/> : (selectedConnection === '' ? 'Create & Connect' : 'Connect')}
                </Button>
              </Group>
            </>
          )}
        </Paper>
      </Paper>
    </Container>
  );
}
